import { useDroppable } from '@dnd-kit/core';
import { generateKebabCase } from '@nexelem/utils';
import { memo } from 'react';
import { NodeType, QueueTaskNode, TimelineNode } from '../types';
import { generateQueues } from '../utils';
import { NodeGantt } from './NodeGantt';
import { cva } from 'class-variance-authority';

export type GanttSingleQueueProps = {
  queueData: ReturnType<typeof generateQueues>;
  index: number;
};

export const ganttSingleQueueVariants = cva('relative flex h-full items-center', {
  variants: {
    isFirst: {
      true: 'border-t border-border-secondary',
    },
    disabled: {
      true: 'bg-surface-container opacity-50',
    },
  },
});

export const GanttSingleQueue = memo(({ queueData, index }: GanttSingleQueueProps) => {
  const { active, setNodeRef } = useDroppable({
    id: generateKebabCase('queue', queueData.id),
  });

  const possibleQueues = active?.data?.current?.possibleQueues ?? [queueData.id];
  const isDisabled = !possibleQueues.includes(queueData.id);

  return (
    <div className={ganttSingleQueueVariants({ isFirst: !!index, disabled: isDisabled })} ref={setNodeRef}>
      {queueData.queueNodes.map((node: TimelineNode, index: number, nodes: TimelineNode[]) => {
        const key = generateKebabCase(queueData.id, node.id, taskExecutionId(node), taskSegmentIndex(node));
        return (
          <NodeGantt
            index={index}
            isQueueDisabled={isDisabled}
            key={key}
            node={node}
            nodes={nodes}
            queueId={queueData.id}
          />
        );
      })}
    </div>
  );
});

GanttSingleQueue.displayName = 'GanttSingleQueue';

const taskExecutionId = (node: TimelineNode) => {
  if (node.type === NodeType.TASK) {
    return node.taskExecutionId;
  }
  if (node.type === NodeType.BATCH) {
    return node.tasks?.map((task) => task.taskExecutionId).join('-');
  }
  if (node.type === NodeType.MULTIPLE) {
    return node.overlappingNodes
      ?.filter((node): node is QueueTaskNode => node.type === NodeType.TASK)
      .map((node) => node.taskExecutionId)
      .join('-');
  }
  return;
};

const taskSegmentIndex = (node: TimelineNode) => {
  if (node.type === NodeType.TASK) {
    return node.segmentIndex;
  }
  if (node.type === NodeType.BATCH) {
    return node.tasks?.map((task) => task.segmentIndex).join('-');
  }
  if (node.type === NodeType.MULTIPLE) {
    return node.overlappingNodes
      ?.filter((node): node is QueueTaskNode => node.type === NodeType.TASK)
      .map((node) => node.segmentIndex)
      .join('-');
  }
  return;
};
